import React from 'react';

// --- Icon Components (inline SVG for better visual appeal) ---
const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const AttachIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
  </svg>
);

const AudioIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const ProjectIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M12 1v4m0 14v4M4.22 4.22l2.83 2.83m9.9 9.9l2.83 2.83M1 12h4m14 0h4M4.22 19.78l2.83-2.83m9.9-9.9l2.83-2.83"></path>
  </svg>
);

// --- Main Component ---
const TimesheetList = ({ formatDate, onDelete, onEdit, t, theme, timesheets }) => {
  // Define CSS custom properties from theme for use in the stylesheet
  const listStyle = {
    '--primary': theme.primary,
    '--surface': theme.background,
    '--border': theme.border,
    '--secondary-text': theme.secondaryText,
    '--error': theme.error,
    '--text': theme.text,
  };

  if (!timesheets || timesheets.length === 0) {
    return (
      <div className="timesheets-empty" style={listStyle}>
        <p>{t('noTimesheets') || 'No timesheets found'}</p>
      </div>
    );
  }

  return (
    <ul className="timesheets-list" style={listStyle}>
      {timesheets.map((timesheet) => (
        <li key={timesheet.id} className="timesheets-item">
          <div className="timesheets-item-head">
            <div className="timesheets-title-group">
              {timesheet.projectName && (
                <span className="timesheets-project">
                  <ProjectIcon />
                  {timesheet.projectName}
                </span>
              )}
              <strong className="timesheets-task">
                {timesheet.taskName || `#${timesheet.taskId ?? '-'}`}
              </strong>
            </div>
            <div className="timesheets-item-actions">
              <span className="timesheets-badge">
                <ClockIcon />
                {timesheet.timeSpent || `${timesheet.hours} ${t('hours') || 'Hours'}`}
              </span>
              <button
                type="button"
                className="timesheets-btn timesheets-btn--edit"
                onClick={() => onEdit(timesheet)}
                aria-label={t('edit') || 'Edit'}
              >
                <EditIcon />
                <span>{t('edit') || 'Edit'}</span>
              </button>
              <button
                type="button"
                className="timesheets-btn timesheets-btn--delete"
                onClick={() => onDelete(timesheet)}
                aria-label={t('delete') || 'Delete'}
              >
                <DeleteIcon />
                <span>{t('delete') || 'Delete'}</span>
              </button>
            </div>
          </div>

          {timesheet.description && (
            <p className="timesheets-description">{timesheet.description}</p>
          )}

          <div className="timesheets-item-meta">
            <span className="timesheets-meta-chip timesheets-item-date">
              <CalendarIcon />
              {formatDate(timesheet.date)}
            </span>
            {timesheet.employeeName && (
              <span className="timesheets-meta-chip">
                <UserIcon />
                {t('employee') || 'Employee'}: {timesheet.employeeName}
              </span>
            )}
            {timesheet.attachments?.length > 0 && (
              <span className="timesheets-meta-chip">
                <AttachIcon />
                {t('attachments') || 'Attachments'}: {timesheet.attachments.length}
              </span>
            )}
            {timesheet.audio?.length > 0 && (
              <span className="timesheets-meta-chip">
                <AudioIcon />
                {t('audioPlayback') || 'Audio'}: {timesheet.audio.length}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default TimesheetList;